/**
 * Tela /areas (TH-53161 / M1.E4.S3.3).
 *
 * Lista compacta com edição inline de nome/slug. Botão "Adicionar
 * área" cria entrada nova com placeholder de slug derivado do nome.
 * Quando a lista está vazia, sugere as 5 áreas default em um banner
 * com botão "Criar áreas padrão" (atalho operacional pra primeiro
 * setup do tenant).
 *
 * Refatorado em TH-54207 para usar <DataTable> com header mono. A
 * edição inline continua: cada linha controla seu próprio modo via
 * AreaRowState (mantida fora do DataTable porque ele só renderiza).
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Pencil, Check, X } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { useAuth } from "@/hooks/useAuth";

interface Area {
  id: string;
  name: string;
  slug: string;
  tv_count: number;
}

interface PagedResponse {
  data: Area[];
  meta: { total: number };
}

const PER_PAGE = 50; // áreas têm N pequeno; per_page maior reduz paginação

const DEFAULT_AREAS: { name: string; slug: string }[] = [
  { name: "Hortifrúti", slug: "hortifruti" },
  { name: "Açougue", slug: "acougue" },
  { name: "Padaria", slug: "padaria" },
  { name: "Mercearia", slug: "mercearia" },
  { name: "Bebidas", slug: "bebidas" },
];

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function normalizeSlug(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

async function fetchAreas(page: number): Promise<PagedResponse> {
  const res = await fetch(`/api/areas?page=${page}&per_page=${PER_PAGE}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PagedResponse;
}

async function createArea(payload: { name: string; slug: string }): Promise<void> {
  const res = await fetch("/api/areas", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

async function patchArea(
  id: string,
  payload: { name?: string; slug?: string },
): Promise<void> {
  const res = await fetch(`/api/areas/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

async function deleteArea(id: string): Promise<void> {
  const res = await fetch(`/api/areas/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

export function AreasPage() {
  const { user } = useAuth();
  const canManage =
    user?.role === "super_admin" || user?.role === "tenant_admin";

  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  // Estado de edição: id da row em edição (null = nenhuma).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftSlug, setDraftSlug] = useState("");
  const [rowError, setRowError] = useState<{ id: string; msg: string } | null>(null);

  const query = useQuery({
    queryKey: ["areas", page],
    queryFn: () => fetchAreas(page),
    staleTime: 30_000,
    enabled: !!user,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["areas"] });

  const save = useMutation({
    mutationFn: async ({ area }: { area: Area }) =>
      patchArea(area.id, {
        name: draftName !== area.name ? draftName : undefined,
        slug: draftSlug !== area.slug ? draftSlug : undefined,
      }),
    onSuccess: () => {
      setEditingId(null);
      setRowError(null);
      invalidate();
    },
    onError: (err: Error, vars) =>
      setRowError({ id: vars.area.id, msg: err.message }),
  });

  const remove = useMutation({
    mutationFn: ({ area }: { area: Area }) => deleteArea(area.id),
    onSuccess: invalidate,
    onError: (err: Error, vars) =>
      setRowError({ id: vars.area.id, msg: err.message }),
  });

  const startEdit = (a: Area) => {
    setEditingId(a.id);
    setDraftName(a.name);
    setDraftSlug(a.slug);
    setRowError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setRowError(null);
  };

  const columns: DataTableColumn<Area>[] = [
    {
      key: "name",
      header: "Nome",
      cell: (a) => {
        const isEditing = editingId === a.id;
        return (
          <>
            {isEditing ? (
              <Input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                className="h-8"
              />
            ) : (
              <span className="font-medium">{a.name}</span>
            )}
            {rowError && rowError.id === a.id ? (
              <span
                role="alert"
                className="mt-1 block text-xs text-[var(--color-destructive)]"
              >
                {rowError.msg}
              </span>
            ) : null}
          </>
        );
      },
    },
    {
      key: "slug",
      header: "Slug",
      mono: true,
      cell: (a) => {
        const isEditing = editingId === a.id;
        return isEditing ? (
          <Input
            value={draftSlug}
            onChange={(e) => setDraftSlug(e.target.value)}
            className="h-8"
            aria-invalid={!SLUG_RE.test(draftSlug) ? true : undefined}
          />
        ) : (
          <code className="text-[var(--color-muted-foreground)]">{a.slug}</code>
        );
      },
    },
    {
      key: "tv_count",
      header: "TVs",
      align: "right",
      mono: true,
      cell: (a) => a.tv_count,
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: "",
            align: "right" as const,
            cell: (a: Area) => {
              const isEditing = editingId === a.id;
              if (isEditing) {
                return (
                  <div className="flex justify-end gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Cancelar"
                      onClick={cancelEdit}
                      disabled={save.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Salvar"
                      onClick={() => save.mutate({ area: a })}
                      disabled={
                        save.isPending ||
                        !draftName.trim() ||
                        !SLUG_RE.test(draftSlug) ||
                        (draftName === a.name && draftSlug === a.slug)
                      }
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  </div>
                );
              }
              return (
                <div className="flex justify-end gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Editar"
                    onClick={() => startEdit(a)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Remover"
                    className="text-[var(--color-destructive)]"
                    disabled={remove.isPending || a.tv_count > 0}
                    title={
                      a.tv_count > 0
                        ? "Há TVs vinculadas — remova ou reatribua antes"
                        : undefined
                    }
                    onClick={() => remove.mutate({ area: a })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const rows = query.data?.data ?? [];
  const subtitle = query.data
    ? `${query.data.meta.total} ${query.data.meta.total === 1 ? "área" : "áreas"}`
    : undefined;

  return (
    <RequireAuth>
      <AppLayout breadcrumb="OPERAÇÃO / ÁREAS" title="Áreas">
        <div className="mx-auto max-w-3xl">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Setores da loja (hortifrúti, açougue, padaria…). Usados em
            templates e na ativação de TVs.
          </p>

          {canManage &&
          query.data &&
          query.data.meta.total === 0 &&
          page === 1 ? (
            <Card className="mt-6">
              <h2 className="text-base font-semibold">Áreas padrão</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Comece com as áreas mais comuns de supermercado:
              </p>
              <ul className="mt-3 flex flex-wrap gap-2">
                {DEFAULT_AREAS.map((d) => (
                  <li
                    key={d.slug}
                    className="rounded-md border border-[var(--color-border)] px-3 py-1 text-sm"
                  >
                    {d.name}{" "}
                    <code className="text-[var(--color-muted-foreground)]">
                      /{d.slug}
                    </code>
                  </li>
                ))}
              </ul>
              <CreateDefaultsButton onSuccess={invalidate} />
            </Card>
          ) : null}

          {canManage ? (
            <div className="mt-6">
              <NewAreaRow onSuccess={invalidate} />
            </div>
          ) : null}

          {query.error ? (
            <div className="mt-4 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
              erro ao carregar
            </div>
          ) : null}

          <div className="mt-4">
            <DataTable<Area>
              columns={columns}
              rows={rows}
              title="Áreas"
              subtitle={subtitle}
              rowKey={(a) => a.id}
              emptyMessage={
                query.isLoading ? "carregando…" : "nenhuma área cadastrada"
              }
            />
          </div>

          {query.data && query.data.meta.total > PER_PAGE ? (
            <div className="mt-4">
              <Pagination
                page={page}
                perPage={PER_PAGE}
                total={query.data.meta.total}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </div>
      </AppLayout>
    </RequireAuth>
  );
}

function CreateDefaultsButton({ onSuccess }: { onSuccess: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: async () => {
      // Cria sequencialmente; backend é rápido. Erro em uma não impede
      // as outras (catch individual mantém a melhor experiência).
      for (const d of DEFAULT_AREAS) {
        await createArea(d).catch(() => {
          /* ignora colisão de slug já existente */
        });
      }
    },
    onSuccess: () => {
      setError(null);
      onSuccess();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="mt-4">
      <Button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
      >
        {mutation.isPending ? "Criando…" : "Criar áreas padrão"}
      </Button>
      {error ? (
        <p
          role="alert"
          className="mt-2 text-xs text-[var(--color-destructive)]"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

function NewAreaRow({ onSuccess }: { onSuccess: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => createArea({ name, slug: slug || normalizeSlug(name) }),
    onSuccess: () => {
      setName("");
      setSlug("");
      setError(null);
      onSuccess();
    },
    onError: (err: Error) => setError(err.message),
  });

  const computedSlug = slug || normalizeSlug(name);
  const slugInvalid = computedSlug !== "" && !SLUG_RE.test(computedSlug);

  return (
    <Card className="p-4">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label
            htmlFor="new-area-name"
            className="text-xs text-[var(--color-muted-foreground)]"
          >
            Nome
          </label>
          <Input
            id="new-area-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Hortifrúti"
          />
        </div>
        <div className="flex-1">
          <label
            htmlFor="new-area-slug"
            className="text-xs text-[var(--color-muted-foreground)]"
          >
            Slug{" "}
            <span className="text-[var(--color-muted-foreground)]">
              (auto: {computedSlug || "—"})
            </span>
          </label>
          <Input
            id="new-area-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder={normalizeSlug(name) || "hortifruti"}
            aria-invalid={slugInvalid ? true : undefined}
          />
        </div>
        <Button
          onClick={() => mutation.mutate()}
          disabled={
            mutation.isPending ||
            !name.trim() ||
            slugInvalid ||
            !computedSlug
          }
        >
          {mutation.isPending ? "Adicionando…" : "Adicionar área"}
        </Button>
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-xs text-[var(--color-destructive)]">
          {error}
        </p>
      ) : null}
    </Card>
  );
}
